import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { incrementViewCount, incrementImportCount } from "./contentStats";
import { TechniquePosition, TechniqueType, TechniqueDifficulty } from "@/types/technique";
import type { Database } from "@/integrations/supabase/types";

type SharedTechniqueRow = Database['public']['Tables']['shared_techniques']['Row'];

/**
 * Generate a slug from a string
 */
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

/**
 * Validates and casts technique position to enum type
 */
function validateTechniquePosition(position: string): TechniquePosition {
  const validPositions: TechniquePosition[] = ['standing', 'top', 'bottom', 'transitional'];
  const lowerPosition = position?.toLowerCase() as TechniquePosition;
  
  if (validPositions.includes(lowerPosition)) {
    return lowerPosition;
  }
  
  console.warn(`Invalid technique position: ${position}, defaulting to 'standing'`);
  return 'standing';
}

/**
 * Validates and casts technique type to enum type
 */
function validateTechniqueType(type: string): TechniqueType {
  const validTypes: TechniqueType[] = [
    'choke', 'joint_lock', 'sweep', 'takedown', 'control', 
    'escape', 'defensive', 'guard_passing', 'other'
  ];
  const lowerType = type?.toLowerCase() as TechniqueType;
  
  if (validTypes.includes(lowerType)) {
    return lowerType;
  }
  
  console.warn(`Invalid technique type: ${type}, defaulting to 'other'`);
  return 'other';
}

/**
 * Validates and casts difficulty to enum type
 */
function validateDifficulty(difficulty: string): TechniqueDifficulty {
  const validDifficulties: TechniqueDifficulty[] = ['beginner', 'intermediate', 'advanced'];
  const lowerDifficulty = difficulty?.toLowerCase() as TechniqueDifficulty;
  
  if (validDifficulties.includes(lowerDifficulty)) {
    return lowerDifficulty;
  }
  
  console.warn(`Invalid difficulty: ${difficulty}, defaulting to 'beginner'`);
  return 'beginner';
}

/**
 * Retrieves a shared technique by its share ID using direct database query
 * @param shareId The unique share ID of the technique
 * @returns The shared technique data if found
 */
export async function getSharedTechnique(shareId: string): Promise<SharedTechniqueRow | null> {
  try {
    console.log("Fetching shared technique with ID:", shareId);
    
    const { data, error } = await supabase
      .from('shared_techniques')
      .select('*')
      .eq('share_id', shareId)
      .single();
    
    if (error) {
      console.error("Error fetching shared technique:", error);
      throw new Error(`Failed to fetch shared technique: ${error.message}`);
    }
    
    if (!data) {
      console.error("No shared technique found with ID:", shareId);
      return null;
    }
    
    // Increment view count using the unified service (non-critical, don't block the response)
    incrementViewCount('technique', shareId).catch(err => {
      console.error("Failed to increment view count:", err);
    });
    
    return data;
  } catch (error: unknown) {
    console.error("Error in getSharedTechnique:", error);
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Imports a shared technique to the user's personal techniques using direct database operations
 * @param shareId The share ID of the technique to import
 * @returns A boolean indicating success or failure
 */
export async function importSharedTechnique(shareId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to import techniques",
        variant: "destructive"
      });
      return false;
    }

    // Get the shared technique using direct database query
    const sharedTechnique = await getSharedTechnique(shareId);
    
    if (!sharedTechnique) {
      toast({
        title: "Import Failed",
        description: "Shared technique not found",
        variant: "destructive"
      });
      return false;
    }

    // Validate and prepare the technique data with proper type casting
    const techniqueData = {
      name: `${sharedTechnique.name} (Imported)`,
      slug: generateSlug(`${sharedTechnique.name} (Imported)`),
      description: sharedTechnique.description || '',
      image_url: sharedTechnique.imageurl || null,
      video_url: sharedTechnique.videourl || null,
      technique_type: validateTechniqueType(sharedTechnique.techniquetype || 'other'),
      technique_position: validateTechniquePosition(sharedTechnique.techniqueposition || 'standing'),
      difficulty: validateDifficulty(sharedTechnique.difficulty || 'beginner'),
      key_points: Array.isArray(sharedTechnique.keypoints) ? sharedTechnique.keypoints : [],
      common_mistakes: Array.isArray(sharedTechnique.commonmistakes) ? sharedTechnique.commonmistakes : [],
      position_id: sharedTechnique.positionid || null,
      is_user_created: true,
      created_by_user_id: user.id,
      is_private: true
    };

    console.log("Inserting technique data:", techniqueData);

    const { error } = await supabase
      .from('techniques')
      .insert(techniqueData);
    
    if (error) {
      console.error("Error importing shared technique:", error);
      toast({
        title: "Import Failed",
        description: error.message || "Could not import the technique",
        variant: "destructive"
      });
      return false;
    }
    
    // Increment import count using the unified service
    incrementImportCount('technique', shareId).catch(err => {
      console.error("Failed to increment import count:", err);
    });
    
    toast({
      title: "Technique Imported",
      description: "The technique has been added to your collection",
    });
    
    return true;
  } catch (error: unknown) {
    console.error("Error in importSharedTechnique:", error);
    toast({
      title: "Import Failed",
      description: error instanceof Error ? error.message : "An unexpected error occurred",
      variant: "destructive"
    });
    return false;
  }
}
